export interface ProductRule {
  id: string;
  product_id: string;
  rule_name: string;
  is_required: boolean;
  price_modifier: string | number;
  price_type: "fixed" | "percentage";
  description?: string | null;
  order_position: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCategory {
  id?: string;
  name?: string;
  description?: string;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  sku: string;
  category_id: string | null;
  base_price: string | number;
  description: string;
  sale_start_date: string | null;
  sale_end_date: string | null;
  attachments: any[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category: ProductCategory | null;
  rules: ProductRule[];
}

export interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProductsApiResponse {
  statusCode: number;
  body: {
    success: boolean;
    data: Product[];
    pagination: PaginationData;
  };
}

export interface ProductApiResponse {
  statusCode: number;
  body: {
    success: boolean;
    data: Product;
  };
}

export interface CreateProductData {
  slug: string;
  name: string;
  sku?: string;
  category_id?: string;
  base_price: number;
  description: string;
  sale_start_date?: string;
  sale_end_date?: string;
  is_active?: boolean | number;
}

export interface UpdateProductData {
  slug: string;
  name?: string;
  sku?: string;
  category_id?: string | null;
  base_price?: number;
  description?: string;
  sale_start_date?: string | null;
  sale_end_date?: string | null;
  is_active?: boolean | number;
}

export interface ProductRuleData {
  product_id: string;
  rule_name: string;
  is_required: boolean;
  price_modifier: number;
  price_type: "fixed" | "percentage";
  description?: string;
  order_position: number;
}

export interface UpdateProductRuleData {
  rule_name?: string;
  is_required?: boolean;
  price_modifier?: number;
  price_type?: "fixed" | "percentage";
  description?: string;
  order_position?: number;
}

export interface GetProductsParams {
  slug: string;
  page?: number;
  limit?: number;
  search?: string;
  category_id?: string;
  is_active?: boolean | number;
}

class ProductService {
  private baseURL = import.meta.env.VITE_CORE_API_URL;

  async getProducts(params: GetProductsParams): Promise<{
    data: Product[];
    pagination: PaginationData;
  }> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("slug", params.slug);

      if (params.page && params.page > 0)
        queryParams.append("page", params.page.toString());
      if (params.limit && params.limit > 0)
        queryParams.append("limit", params.limit.toString());
      if (params.search && params.search.trim() !== "")
        queryParams.append("search", params.search);

      if (params.is_active !== undefined && params.is_active !== null) {
        const isActiveValue =
          params.is_active === true
            ? 1
            : params.is_active === false
              ? 0
              : params.is_active;
        queryParams.append("is_active", isActiveValue.toString());
      }
      
      queryParams.append("_t", Date.now().toString());

      const url = `${this.baseURL}/products?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        return {
          data: apiResponse.data,
          pagination: apiResponse.pagination || {
            total: apiResponse.data.length,
            page: params.page || 1,
            limit: params.limit || 50,
            totalPages: Math.ceil(
              apiResponse.data.length / (params.limit || 50),
            ),
          },
        };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return {
          data: apiResponse.body.data,
          pagination: apiResponse.body.pagination || {
            total: apiResponse.body.data.length,
            page: params.page || 1,
            limit: params.limit || 50,
            totalPages: Math.ceil(
              apiResponse.body.data.length / (params.limit || 50),
            ),
          },
        };
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async getProductById(id: string, slug: string): Promise<Product> {
    try {
      const url = `${this.baseURL}/products/${id}?slug=${slug}&_t=${Date.now()}`;

      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async createProduct(productData: CreateProductData): Promise<Product> {
    try {
      const url = `${this.baseURL}/products`;

      const dataToSend = {
        ...productData,
        is_active:
          productData.is_active === true
            ? 1
            : productData.is_active === false
              ? 0
              : productData.is_active || 1,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (
        (apiResponse.statusCode === 200 || apiResponse.statusCode === 201) &&
        apiResponse.body?.success
      ) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async updateProduct(
    id: string,
    productData: UpdateProductData,
  ): Promise<Product> {
    try {
      const url = `${this.baseURL}/products/${id}`;

      const dataToSend: any = { ...productData };
      if (productData.is_active !== undefined) {
        dataToSend.is_active =
          productData.is_active === true
            ? 1
            : productData.is_active === false
              ? 0
              : productData.is_active;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteProduct(id: string, slug: string): Promise<boolean> {
    try {
      const url = `${this.baseURL}/products/${id}?slug=${slug}`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success) {
        return true;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return true;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async addProductRule(ruleData: ProductRuleData): Promise<ProductRule> {
    try {
      const url = `${this.baseURL}/products/rules`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (
        (apiResponse.statusCode === 200 || apiResponse.statusCode === 201) &&
        apiResponse.body?.success
      ) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${
            apiResponse.body?.message || apiResponse.message || "Unknown error"
          }`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async updateProductRule(
    id: string,
    ruleData: UpdateProductRuleData,
  ): Promise<ProductRule> {
    try {
      const url = `${this.baseURL}/products/rules/${id}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return apiResponse.body.data;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteProductRule(id: string): Promise<boolean> {
    try {
      const url = `${this.baseURL}/products/rules/${id}`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success) {
        return true;
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return true;
      } else {
        throw new Error(
          `API error: ${apiResponse.body?.message || apiResponse.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      throw error;
    }
  }

  transformPriceForUI(price: string | number): number {
    return typeof price === "string" ? parseFloat(price) : price;
  }

  formatPrice(price: number, currency: string = "USD"): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }

  calculateProductWithRules(
    product: Product,
    quantity: number = 1,
    selectedRules: string[] = [],
  ): number {
    let total = this.transformPriceForUI(product.base_price);

    product.rules.forEach((rule) => {
      if (selectedRules.includes(rule.id) || rule.is_required) {
        const modifier = this.transformPriceForUI(rule.price_modifier);

        switch (rule.price_type) {
          case "fixed":
            total += modifier;
            break;
          case "percentage":
            total += (total * modifier) / 100;
            break;
        }
      }
    });

    return total * quantity;
  }

  getActiveProducts(products: Product[]): Product[] {
    return products.filter((product) => product.is_active);
  }

  getProductsByCategory(products: Product[], categoryId: string): Product[] {
    return products.filter((product) => product.category_id === categoryId);
  }

  searchProducts(products: Product[], searchTerm: string): Product[] {
    const term = searchTerm.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(term) ||
        product.sku.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term),
    );
  }

  getProductsWithoutCategoryFilter(products: Product[]): Product[] {
    return products.filter(
      (product) => !product.category_id || product.category_id === "",
    );
  }
}

export const productService = new ProductService();

